(function () {
  'use strict';

  const configElement = document.getElementById('auto-gift-config');
  let config;

  try {
    config = JSON.parse(configElement?.textContent || '{}');
  } catch (error) {
    console.error('[Auto Gift] Invalid configuration.', error);
    return;
  }

  const promotions = (config.promotions || [])
    .map((promotion) => ({
      ...promotion,
      giftVariantId: String(promotion.giftVariantId || ''),
      requiredDistinctProducts: Math.max(1, Number(promotion.requiredDistinctProducts) || 1),
      triggerProductIds: new Set((promotion.triggerProductIds || []).map(String))
    }))
    .filter((promotion) => promotion.enabled && promotion.giftVariantId && promotion.triggerProductIds.size);

  if (!promotions.length) {
    return;
  }

  const giftPropertyName = '_auto_gift';
  const giftPropertyValue = 'true';
  const promotionPropertyName = '_auto_gift_promotion';
  const declinedCartStorageKey = 'auto_gift_declined_cart';
  const originalFetch = window.fetch.bind(window);
  const promotionState = new Map();
  let syncing = false;
  let syncAgain = false;

  function getCartUrl(endpoint) {
    const cartEndpoint = endpoint ? `cart/${endpoint}.js` : 'cart.js';

    return `${window.Shopify?.routes?.root || '/'}${cartEndpoint}`;
  }

  function isCartMutation(input, options) {
    const method = String(options?.method || input?.method || 'GET').toUpperCase();

    if (method === 'GET') {
      return false;
    }

    const inputUrl = typeof input === 'string' ? input : input?.url;

    if (!inputUrl) {
      return false;
    }

    const url = new URL(inputUrl, window.location.origin);

    return url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname);
  }

  async function request(endpoint, options = {}) {
    const response = await originalFetch(getCartUrl(endpoint), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || `Cart request failed: ${endpoint}`);
    }

    return data;
  }

  function getCart() {
    return request('');
  }

  function getSectionsToRender() {
    const sections = [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
    ];
    const cartItemsSectionId = document.getElementById('main-cart-items')?.dataset.id;
    const cartFooterSectionId = document.getElementById('main-cart-footer')?.dataset.id;

    if (cartItemsSectionId) {
      sections.push({ id: 'main-cart-items', section: cartItemsSectionId, selector: '.js-contents' });
    }

    if (cartFooterSectionId) {
      sections.push({ id: 'main-cart-footer', section: cartFooterSectionId, selector: '.js-contents' });
    }

    return sections.slice(0, 5);
  }

  function renderSections(cart, sections) {
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      const sectionHtml = cart.sections?.[section.section];

      if (!element || !sectionHtml) {
        return;
      }

      const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(section.selector);
      const elementToReplace = element.querySelector(section.selector) || element;

      if (parsedSection) {
        elementToReplace.innerHTML = parsedSection.innerHTML;
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));
  }

  function updateLines(updates) {
    return request('update', {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  }

  function addGift(promotion) {
    return request('add', {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: promotion.giftVariantId,
            quantity: 1,
            properties: {
              [giftPropertyName]: giftPropertyValue,
              [promotionPropertyName]: promotion.id
            }
          }
        ]
      })
    });
  }

  function isGift(item, promotion) {
    return String(item.variant_id) === promotion.giftVariantId;
  }

  function isAutomaticGift(item, promotion) {
    if (!isGift(item, promotion) || String(item.properties?.[giftPropertyName]).toLowerCase() !== giftPropertyValue) {
      return false;
    }

    const promotionId = item.properties?.[promotionPropertyName];

    return promotionId === promotion.id || (!promotionId && promotion.id === promotions[0].id);
  }

  function isPromotionActive(cart, promotion) {
    const distinctProductIds = new Set();

    cart.items.forEach((item) => {
      const productId = String(item.product_id);

      if (Number(item.quantity) > 0 && promotion.triggerProductIds.has(productId)) {
        distinctProductIds.add(productId);
      }
    });

    return distinctProductIds.size >= promotion.requiredDistinctProducts;
  }

  function getDeclinedCartToken(promotion) {
    const state = promotionState.get(promotion.id) || {};

    try {
      return window.localStorage.getItem(`${declinedCartStorageKey}_${promotion.id}`);
    } catch (error) {
      return state.declinedCartToken || null;
    }
  }

  function setGiftDeclined(cart, promotion) {
    const state = promotionState.get(promotion.id) || {};
    state.declinedCartToken = cart.token;
    promotionState.set(promotion.id, state);

    try {
      window.localStorage.setItem(`${declinedCartStorageKey}_${promotion.id}`, cart.token);
    } catch (error) {
      return;
    }
  }

  function clearGiftDeclined(cart, promotion) {
    if (getDeclinedCartToken(promotion) !== cart.token) {
      return;
    }

    const state = promotionState.get(promotion.id) || {};
    state.declinedCartToken = null;
    promotionState.set(promotion.id, state);

    try {
      window.localStorage.removeItem(`${declinedCartStorageKey}_${promotion.id}`);
    } catch (error) {
      return;
    }
  }

  function isGiftDeclined(cart, promotion) {
    return Boolean(cart.token) && getDeclinedCartToken(promotion) === cart.token;
  }

  async function refreshCart() {
    const sections = getSectionsToRender();
    const cart = await request('update', {
      method: 'POST',
      body: JSON.stringify({
        sections: sections.map((section) => section.section),
        sections_url: window.location.pathname
      })
    });

    renderSections(cart, sections);
  }

  async function normalizeGift(cart, promotion) {
    const automaticGiftLines = cart.items.filter((item) => isAutomaticGift(item, promotion));
    const updates = {};

    if (automaticGiftLines.length) {
      if (Number(automaticGiftLines[0].quantity) !== 1) {
        updates[automaticGiftLines[0].key] = 1;
      }

      automaticGiftLines.slice(1).forEach((item) => {
        updates[item.key] = 0;
      });
    }

    if (Object.keys(updates).length) {
      await updateLines(updates);
    }

    if (!automaticGiftLines.length) {
      await addGift(promotion);
    }

    return Object.keys(updates).length > 0 || automaticGiftLines.length === 0;
  }

  async function removeGift(cart, promotion) {
    const updates = {};

    cart.items.filter((item) => isAutomaticGift(item, promotion)).forEach((item) => {
      updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) {
      return false;
    }

    await updateLines(updates);
    return true;
  }

  async function syncPromotion(cart, promotion) {
    const state = promotionState.get(promotion.id) || { automaticGiftWasPresent: false };
    const promotionActive = isPromotionActive(cart, promotion);
    const automaticGiftPresent = cart.items.some((item) => isAutomaticGift(item, promotion));
    let changed = false;

    if (promotionActive && state.automaticGiftWasPresent && !automaticGiftPresent) {
      setGiftDeclined(cart, promotion);
    }

    if (!promotionActive) {
      clearGiftDeclined(cart, promotion);
      changed = await removeGift(cart, promotion);
      state.automaticGiftWasPresent = false;
    } else if (isGiftDeclined(cart, promotion)) {
      changed = await removeGift(cart, promotion);
      state.automaticGiftWasPresent = false;
    } else {
      changed = await normalizeGift(cart, promotion);
      state.automaticGiftWasPresent = true;
    }

    promotionState.set(promotion.id, state);
    return changed;
  }

  async function syncGifts() {
    if (syncing) {
      syncAgain = true;
      return;
    }

    syncing = true;

    try {
      let cart = await getCart();
      let changed = false;

      for (const promotion of promotions) {
        if (await syncPromotion(cart, promotion)) {
          changed = true;
          cart = await getCart();
        }
      }

      if (changed) {
        await refreshCart();
      }
    } catch (error) {
      console.error('[Auto Gift]', error);
    } finally {
      syncing = false;

      if (syncAgain) {
        syncAgain = false;
        queueMicrotask(syncGifts);
      }
    }
  }

  function initialize() {
    window.fetch = async function (...args) {
      const response = await originalFetch(...args);

      if (response.ok && isCartMutation(args[0], args[1])) {
        setTimeout(syncGifts, 0);
      }

      return response;
    };

    syncGifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
